import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { TextField } from '@/components/forms/TextField';

export interface CentFormValues {
  name: string;
  relatedSymbol: string;
  contractSize: string; // kept as string for the input; coerced by the caller
}

const schema = z.object({
  name: z.string().trim().min(1, 'Please enter a name.'),
  relatedSymbol: z.string().trim().min(1, 'Please enter a related symbol.'),
  contractSize: z
    .string()
    .trim()
    .min(1, 'Please enter a contract size.')
    .refine((v) => {
      const n = Number(v.replace(/,/g, ''));
      return Number.isFinite(n);
    }, 'Please enter a valid number.'),
});

interface CentFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  initial?: { name: string; relatedSymbol: string; contractSize: number };
  busy?: boolean;
  onSubmit: (values: CentFormValues) => void;
  onClose: () => void;
}

export function CentFormModal({
  show,
  mode,
  initial,
  busy = false,
  onSubmit,
  onClose,
}: CentFormModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial?.name ?? '',
      relatedSymbol: initial?.relatedSymbol ?? '',
      contractSize: initial?.contractSize != null ? String(initial.contractSize) : '',
    },
  });

  return (
    <Modal
      show={show}
      title={mode === 'create' ? 'Create New' : 'Edit Cent'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="cent-form" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="cent-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {mode === 'create' ? (
          <TextField label="Name" autoComplete="off" error={errors.name?.message} {...register('name')} />
        ) : (
          // Name is read-only after creation (backend keeps it as the key).
          <div className="mb-3">
            <label className="form-label">Name</label>
            <input className="form-control" value={initial?.name ?? ''} disabled readOnly />
          </div>
        )}

        <TextField
          label="Related Symbol"
          autoComplete="off"
          error={errors.relatedSymbol?.message}
          {...register('relatedSymbol')}
        />

        <TextField
          label="Contract Size"
          autoComplete="off"
          error={errors.contractSize?.message}
          {...register('contractSize')}
        />
      </form>
    </Modal>
  );
}
